import { useQuery } from "@tanstack/react-query";
import type {
  DashboardDataReq,
  DashboardSalesResp,
} from "adl-gen/ferovinum/app/api";
import { getLocalTodayString } from "../../../utils";
import { useAppService } from "../../use-app-service";
import { dashboardKeys } from "../query-keys";

export interface SalesDataPoint {
  month: string;
  customerSales: number;
  stockRepurchased: number;
}

interface SalesDataParams {
  organisationId: string;
  dateRange?: string;
  enabled?: boolean;
}

export const useDashboardSalesData = ({
  organisationId,
  dateRange,
  enabled = true,
}: SalesDataParams) => {
  const service = useAppService();

  return useQuery({
    queryKey: dashboardKeys.salesData(organisationId, dateRange),
    queryFn: async (): Promise<SalesDataPoint[]> => {
      const todayString = getLocalTodayString();

      const request: DashboardDataReq = {
        organisationId: organisationId,
        asOn: todayString,
      };

      const response: DashboardSalesResp[] =
        await service.getDashboardSalesData(request);

      // Filter to only include complete data points
      return response
        .filter((item) => item.sales != null && item.repurchases != null)
        .map((item) => ({
          month: item.month,
          customerSales: Number(item.sales),
          stockRepurchased: Number(item.repurchases),
        }));
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
